import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'components/Button'
import PublicModal from 'components/PublicModal'
import { useNotifier } from 'react-headless-notifier'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { useForm } from 'react-hook-form'
import { ArrowLeftIcon, MessengerIcon, PhoneCallbackIcon, PhoneIcon, CheckCircleIcon } from 'components/icons'
import StatusPanel from 'components/StatusPanel'
import CountryListbox from 'components/form/CountryListbox'
import { useMutation } from 'react-query'
import ApiClient from 'data/api/api_client'
import Notification from 'components/Notification'
import Form from 'components/form'
import DayPickerDoubleDesign from 'components/dates/DayPickerDoubleDesign'
import { formatDateReadable } from 'data/helpers/dates'
import Label from 'components/form/Label'
import { useSelector } from 'react-redux'
import CenteredCustomerLoginModal from 'components/modals/getHelp/CenteredCustomerLoginModal'
import { config } from 'data/config'
import Script from 'next/script'
import { triggerInquiryForm } from 'data/helpers/gtm'

const GetHelpModal = ({ modalOpen, onClose, initialType, region = null }) => {
  const { notify } = useNotifier()
  const handleError = useErrorHandler()

  const { user } = useSelector(state => ({
    user: state.auth.user,
  }))

  const [modalType, setModalType] = useState(initialType)
  const [isSent, setIsSent] = useState(false)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(true)

  const { register, formState: { errors, isSubmitting }, handleSubmit, setError, clearErrors, reset, setValue, getValues } = useForm({})

  const { mutate: createInquiryMutation, isLoading: isInquiryMutationLoading } = useMutation(
    (details) => {
      if (modalType === 'callback') {
        return ApiClient.inquiries.createInquiry({
          first_name: details?.first_name,
          last_name: details?.last_name,
          phone_number: details?.phone_number,
          country_code: details?.country_code,
          message: details?.message,
        }, 'callback')
      } else {
        return ApiClient.inquiries.createInquiry({
          arrival: dates?.from,
          departure: dates?.to,
          guests: details?.guests,
          budget: details?.budget,
          message: details?.message,
          destination: region?.value ?? null,
          destination_id: region?.id ?? null,
        }, 'general')
      }
    },
    {
      onSuccess: (_, variables) => {
        if (modalType === 'callback') {
          triggerInquiryForm({
            email: user?.email ?? null,
            countryCode: variables?.country_code,
            phone: variables?.phone_number,
          })
        } else {
          triggerInquiryForm({
            email: user.email,
            countryCode: user.phone_country_code,
            phone: user.phone,
          })
        }

        notify(
          <Notification type="success" message="Inquiry sent!" />,
        )
        setIsSent(true)
        reset()
      },
      onError: (error) => {
        clearErrors()
        handleError(error, setError)
      },
    },
  )

  const recaptchaVerifyMutation = useMutation(
    (token) => ApiClient.auth.verifyWithRecaptcha(token),
    {
      onSuccess: (data) => {
        if (data && data?.success === true && data?.score >= 0.5) {
          createInquiryMutation(getValues())
        } else {
          notify(
            <Notification type="error" message="There was an error submitting this request, please try again" />,
          )
        }
      },
      onError: () => {
        notify(
          <Notification type="error" message="There was an error submitting this request, please try again" />,
        )
      },
    },
  )

  const onSubmit = (details) => {
    if (recaptchaLoaded) {
      if (config.recaptchaSiteKey && !user) {
        window.grecaptcha.ready(function () {
          window.grecaptcha.execute(config.recaptchaSiteKey, { action: 'submit_callback_inquiry' }).then(function (token) {
            recaptchaVerifyMutation.mutate(token)
          })
        })
      } else {
        createInquiryMutation(details)
      }
    } else {
      notify(
        <Notification type="error" message="There was an error submitting this request, please try again" />,
      )
    }
  }

  const [selectedCountry, setSelectedCountry] = useState(null)
  const [giveUsACallHovered, setGiveUsACallHovered] = useState(false)

  const [page, setPage] = useState(0)

  const resetPageOnClose = () => {
    setSelectedCountry(null)
    setDates({
      from: null,
      to: null,
    })
    onClose()
    reset()
    setPage(0)
  }

  useEffect(() => {
    if (modalOpen) {
      setIsSent(false)
    }
  }, [modalOpen])

  useEffect(() => {
    if (selectedCountry) {
      setValue('country_code', selectedCountry?.phone)
    }
    clearErrors('country_code')
  }, [selectedCountry])

  useEffect(() => {
    setModalType(initialType)
  }, [initialType])

  const [dates, setDates] = useState({
    from: undefined,
    to: undefined,
  })

  const [loginModalOpen, setLoginModalOpen] = useState(false)

  const changeModalType = (type) => {
    setSelectedCountry(null)
    setDates({
      from: null,
      to: null,
    })
    reset()
    if (type === 'general' && !user) {
      setLoginModalOpen(true)
    } else {
      setModalType(type)
    }
  }

  useEffect(() => {
    clearErrors('arrival')
  }, [dates])

  useEffect(() => {
    if (user) {
      setValue('first_name', user?.first_name)
      setValue('last_name', user?.last_name)
      setSelectedCountry({
        name: '',
        emoji: '',
        phone: user?.phone_country_code,
      })
      setValue('phone_number', user?.phone)
    }
  }, [user, modalType])

  return (
    <PublicModal
      isOpen={modalOpen}
      onClose={resetPageOnClose}
      className={`${isSent ? 'max-w-[450px]' : 'max-w-6xl'} ${loginModalOpen ? 'hidden' : ''}`}
      innerPadding={`${isSent ? '' : '!px-20 !pb-25 lg:!px-50 lg:!pb-50 lg:!pt-60'}`}
    >
      {config.recaptchaSiteKey && !user
        && <Script
          id="grecaptcha"
          src={`https://www.google.com/recaptcha/api.js?render=${config.recaptchaSiteKey}`}
          onLoad={() => {
            setRecaptchaLoaded(true)
          }}
        />
      }
      {isSent
        ? <StatusPanel
          icon={<CheckCircleIcon />}
          title="Message Sent"
          description="Thanks for reaching out, we will be in touch shortly!"
        />
        : <div className="flex flex-col antialiased lg:flex-row gap-50">
          <button type="button" className="absolute top-20 left-20" onClick={() => page > 0 ? setPage(page - 1) : onClose()}>
            <ArrowLeftIcon />
          </button>

          <div className="flex flex-col w-full gap-15">
            <p className="font-bold uppercase text-grey-750 text-14">
              {modalType === 'callback'
                ? 'Request a callback'
                : 'Send us a message'
              }
            </p>
            <h3 className="lg:max-w-[400px] text-32 md:text-36 xl:text-48 font-bold font-serif leading-title mb-10 md:mb-30 lg:mr-100">
              {modalType === 'callback'
                ? 'Let us know when you’re available for a call.'
                : <span>How can we help?<br />We’re here to assist you.</span>
              }
            </h3>
            <div className="flex flex-col gap-10 md:gap-15">
              {modalType === 'general'
                && <button
                  type="button"
                  className="md:max-w-[400px] rounded-5 flex py-20 px-25 gap-25 text-grey-900 hover:text-grey-700 border border-black border-opacity-40 hover:border-opacity-20"
                  onClick={() => changeModalType('callback')}
                >
                  <div className="mt-5 text-18">
                    <PhoneCallbackIcon />
                  </div>
                  <div className="text-left">
                    <h4 className="text-18">Request a callback</h4>
                    <p className="mt-10 text-grey-1000 text-opacity-40 text-16">Our line is busy or it’s outside of office hours? Request a callback here.</p>
                  </div>
                </button>
              }
              <a
                className="md:max-w-[400px] h-65 rounded-5 flex items-center py-20 px-25 gap-25 hover:bg-primary text-grey-900 hover:text-white cursor-pointer border border-black border-opacity-40 hover:border-primary hover:border-opacity-100"
                onMouseEnter={() => setGiveUsACallHovered(true)}
                onMouseLeave={() => setGiveUsACallHovered(false)}
                href={`tel:${config.amaSelectionsPhoneNumber}`}
              >
                <PhoneIcon />
                <p className="text-18 focus:outline-none">{giveUsACallHovered ? config.amaSelectionsDisplayPhoneNumber : 'Give us a call'}</p>
              </a>
              {modalType === 'callback'
                && <button
                  type="button"
                  className="md:max-w-[400px] h-65 rounded-5 flex items-center py-20 px-25 gap-25 text-grey-900 hover:text-grey-700 border border-black border-opacity-40 hover:border-opacity-20"
                  onClick={() => changeModalType('general')}
                >
                  <MessengerIcon />
                  <h4 className="text-18">Send us a message</h4>
                </button>
              }
            </div>
          </div>
          <>
            {modalType === 'callback'
              ? <div className="w-full">
                <form
                  id="callback-inquiry-form"
                  className="flex flex-col gap-20"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex flex-col gap-20 sm:flex-row">
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Name</Label>
                      <Form.Input
                        style="customer"
                        placeholder="Your Name"
                        {...register('first_name')}
                        error={errors?.first_name?.message}
                      />
                    </div>
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Last Name</Label>
                      <Form.Input
                        style="customer"
                        placeholder="Your Last Name"
                        {...register('last_name')}
                        error={errors?.last_name?.message}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-20 sm:flex-row">
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Country Code</Label>
                      <CountryListbox
                        selectedCountry={selectedCountry}
                        setSelectedCountry={setSelectedCountry}
                        label="Country"
                      />
                      {
                        errors?.country_code && !selectedCountry
                          ? <span className="text-red text-14">{errors?.country_code?.message}</span>
                          : null
                      }
                    </div>
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Phone Number</Label>
                      <Form.Input
                        style="customer"
                        placeholder="Your Phone Number"
                        {...register('phone_number')}
                        error={errors?.phone_number?.message}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-[9px] text-grey-800 !text-14">Message</Label>
                    <Form.Textarea
                      className="ring-input-border ring-opacity-100 focus:ring-primary focus:ring-opacity-50"
                      placeholder="Let us know when and where you'd like to travel to and what the occasion is ..."
                      {...register('message')}
                      error={errors?.message?.message}
                      style="customer"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="font-bold uppercase text-18"
                    isLoading={isSubmitting || !recaptchaLoaded || isInquiryMutationLoading}
                    disabled={isSubmitting}
                  >
                    Request Callback
                  </Button>

                </form>
              </div>
              : modalType === 'general'
              && <div className="w-full">
                <form
                  id="general-inquiry-form"
                  className="flex flex-col gap-20"
                  onSubmit={handleSubmit(createInquiryMutation)}
                >
                  <DayPickerDoubleDesign
                    variant="public_booking"
                    errors={errors}
                    classNames="w-full gap-20 flex flex-col sm:flex-row items-center [&_input]:!py-[13px]"
                    subClassNames="w-full ring ring-input-border border-none h-50 rounded-4 antialiased"
                    label={['Enter Date', 'Enter Date']}
                    overheadLabel={{
                      from: 'Arrival',
                      to: 'Departure',
                    }}
                    overheadLabelClassNames="mb-[9px] text-grey-800 !text-14"
                    formatDate={formatDateReadable}
                    align="right"
                    onDayChange={(dates) => {
                      clearErrors('arrival')
                      clearErrors('departure')
                      setDates(dates)
                    }}
                  />

                  <div className="flex flex-col gap-20 sm:flex-row">
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Guests</Label>
                      <Form.Input
                        style="customer"
                        placeholder="Number Of Guests"
                        {...register('guests')}
                        error={errors?.guests?.message}
                      />
                    </div>
                    <div className="w-full">
                      <Label className="mb-[9px] text-grey-800 !text-14">Budget</Label>
                      <Form.Input
                        style="customer"
                        placeholder="Your Budget"
                        {...register('budget')}
                        error={errors?.budget?.message}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-[9px] text-grey-800 !text-14">Message</Label>
                    <Form.Textarea
                      className="ring-input-border ring-opacity-100 focus:ring-primary focus:ring-opacity-50"
                      placeholder="Let us know when and where you'd like to travel to and what the occasion is ..."
                      {...register('message')}
                      error={errors?.message?.message}
                      style="customer"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="font-bold uppercase text-18"
                    isLoading={isSubmitting || !recaptchaLoaded || isInquiryMutationLoading}
                    disabled={isSubmitting}
                  >
                    Send Message
                  </Button>

                </form>
              </div>
            }</>
        </div>
      }

      <CenteredCustomerLoginModal modalOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </PublicModal>
  )
}

GetHelpModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  region: PropTypes.object,
  initialType: PropTypes.string,
}

export default GetHelpModal
